'use client'

import * as React from 'react'
import Image from 'next/image'
import Autoplay from 'embla-carousel-autoplay'
import { ChevronLeft, ChevronRight, ImageIcon } from 'lucide-react'

import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { type CarouselApi, Carousel, CarouselContent, CarouselItem } from '@/components/ui/carousel'

export type Testimonial = {
  quote: string
  author: string
  organization: string
  /** Partner logo / photo shown on the left. Real content is populated here. */
  image?: { src: string; alt: string }
}

export type TestimonialCarouselProps = {
  testimonials: Testimonial[]
  /** Auto-advance every 6s, pausing on hover / interaction. */
  autoplay?: boolean
  className?: string
}

export function TestimonialCarousel({
  testimonials,
  autoplay = false,
  className,
}: TestimonialCarouselProps) {
  const [api, setApi] = React.useState<CarouselApi>()

  // Create the plugin once via state (reading a ref during render is disallowed).
  const [autoplayPlugin] = React.useState(() =>
    Autoplay({ delay: 6000, stopOnInteraction: true, stopOnMouseEnter: true }),
  )

  // Read embla's selection/snap state through an external store, not an effect.
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      if (!api) return () => {}
      api.on('select', onChange).on('reInit', onChange)
      return () => {
        api.off('select', onChange).off('reInit', onChange)
      }
    },
    [api],
  )
  const selected = React.useSyncExternalStore(
    subscribe,
    () => api?.selectedScrollSnap() ?? 0,
    () => 0,
  )
  const snapCount = React.useSyncExternalStore(
    subscribe,
    () => api?.scrollSnapList().length ?? testimonials.length,
    () => testimonials.length,
  )

  const hasControls = testimonials.length > 1

  return (
    // No `overflow-hidden` here: combined with the `@container` below, it collapses
    // this Card's height in Chromium (container-query + overflow-hidden ancestor
    // layout bug). Content is padded well inside the rounded corners, and the
    // slide-clipping is handled by CarouselContent's own overflow-hidden, so it's
    // not needed for correctness — only for edge-to-edge media, which this lacks.
    <Card className={cn('gap-0 p-0', className)}>
      <div className="@container relative">
        <Carousel
          setApi={setApi}
          opts={{ loop: hasControls }}
          plugins={autoplay && hasControls ? [autoplayPlugin] : undefined}
          aria-label="Member testimonials"
        >
          <CarouselContent>
            {testimonials.map((t, i) => (
              <CarouselItem key={`${t.author}-${i}`}>
                <div className="grid items-center gap-8 px-10 py-8 @2xl:grid-cols-[12rem_minmax(0,1fr)] @2xl:gap-14 @2xl:px-16 @2xl:py-10">
                  {/* Image / logo */}
                  <div className="flex items-center justify-center">
                    {t.image ? (
                      <Image
                        src={t.image.src}
                        alt={t.image.alt}
                        width={240}
                        height={160}
                        className="h-auto w-full max-w-48 object-contain"
                      />
                    ) : (
                      <div className="bg-muted text-muted-foreground flex aspect-4/3 w-full max-w-48 items-center justify-center rounded-lg">
                        <ImageIcon className="size-7" />
                      </div>
                    )}
                  </div>

                  {/* Quote */}
                  <blockquote className="flex min-w-0 flex-col">
                    <p className="text-foreground text-base leading-relaxed font-semibold text-pretty @2xl:text-lg">
                      &ldquo;{t.quote}&rdquo;
                    </p>
                    <footer className="text-muted-foreground mt-2 text-sm">
                      &mdash; {t.author} &ndash; {t.organization}
                    </footer>
                  </blockquote>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {/* Edge arrows */}
        {hasControls && (
          <>
            <button
              type="button"
              aria-label="Previous testimonial"
              onClick={() => api?.scrollPrev()}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 absolute top-1/2 left-1 grid size-9 -translate-y-1/2 place-items-center rounded-full transition-colors outline-none focus-visible:ring-2 md:left-3"
            >
              <ChevronLeft className="size-6" />
            </button>
            <button
              type="button"
              aria-label="Next testimonial"
              onClick={() => api?.scrollNext()}
              className="text-muted-foreground hover:bg-accent hover:text-accent-foreground focus-visible:ring-ring/50 absolute top-1/2 right-1 grid size-9 -translate-y-1/2 place-items-center rounded-full transition-colors outline-none focus-visible:ring-2 md:right-3"
            >
              <ChevronRight className="size-6" />
            </button>
          </>
        )}
      </div>

      {/* Pagination dots */}
      {hasControls && (
        <div className="flex justify-center gap-2 pb-6">
          {Array.from({ length: snapCount }).map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Go to testimonial ${i + 1}`}
              aria-current={i === selected}
              onClick={() => api?.scrollTo(i)}
              className={cn(
                'h-2 rounded-full transition-[width,background-color] motion-reduce:transition-none',
                i === selected
                  ? 'bg-brand-maroon w-6'
                  : 'bg-brand-maroon/30 hover:bg-brand-maroon/60 w-2',
              )}
            />
          ))}
        </div>
      )}
    </Card>
  )
}

/** Dummy testimonials for UI preview. Real data (incl. images) will be wired from the backend later. */
export const dummyTestimonials: Testimonial[] = [
  {
    quote:
      "I've always been scared of booking air as my knowledge/time is limited, however, after getting training, I surprised even myself that I was able to book my clients. The system is very intuitive and with enough time to explore and practice, anyone can do it. I'm so happy with this program.",
    author: 'Sarah Lloyd',
    organization: 'Admiral of the Fleet',
  },
  {
    quote:
      'The support from the Ensemble team has been outstanding. Our bookings are up and my advisors feel confident using the platform every single day. It has genuinely changed how we run the agency.',
    author: 'Marcus Chen',
    organization: 'Horizon Travel Collective',
  },
  {
    quote:
      'What used to take hours now takes minutes. The tools are intuitive, the training is excellent, and the community of advisors makes every question easy to answer.',
    author: 'Priya Nair',
    organization: 'Wanderlust Journeys',
  },
]
